const STATES = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT'];
const ABBREVIATIONS = {
  'RD': 'Road',
  'ST': 'Street',
  'AVE': 'Avenue',
  'DR': 'Drive',
  'CT': 'Court',
  'PL': 'Place',
  'LANE': 'Lane',
  'HWY': 'Highway',
  'PDE': 'Parade',
  'TCE': 'Terrace',
  'CL': 'Close',
  'GR': 'Grove',
  'RDG': 'Ridge'
};

/**
 * Formats an Australian address string:
 * - Converts "UNIT X, Y" to "X/Y"
 * - Removes leading "UNIT "
 * - Converts to Title Case
 * - Keeps states uppercase (NSW, VIC, etc.)
 * - Expands common abbreviations (RD -> Road, ST -> Street)
 * - Removes CRM prefixes like "Off-Market Seller" and "Apartment at"
 * - Optionally removes state and postcode
 * 
 * @param {string} addressString - The raw address string from API
 * @param {object} options - Options object
 * @param {boolean} options.short - If true, removes state and postcode
 * @returns {string} - Formatted address string
 */
export const formatAddress = (addressString, options = {}) => {
  const { short = false } = options;
  if (!addressString) return "";

  // 1. Clean up common CRM prefixes and standardize separators
  let formatted = addressString.trim()
    .replace(/^(?:Off-Market\s*Seller\s*[-–—:]*\s*|Apartment\s+at\s*(?:UNIT\s+)?|Apartment\s+at\s*)/i, '')
    .replace(/\s+/g, ' ');

  // 2. Handle "UNIT X, [Complex,] Y Street" or "SHOP X, [Complex,] Y Street"
  // This converts "UNIT 10, 322 SYDENHAM RD" to "10/322 SYDENHAM RD"
  // and "UNIT 8, ONE SYDNEY, 3 SYDNEY AV" to "8/3 SYDNEY AV"
  formatted = formatted
    .replace(/^(?:UNIT|SHOP|STE|SUITE|LEVEL)\s*(\d+)\s*[,/]\s*(?:[^,]+,)?\s*(\d+)\s+/i, '$1/$2 ')
    .replace(/^(?:UNIT|SHOP|STE|SUITE|LEVEL)\s*(\d+)\s+(?:[^,]+,)?\s*(\d+)\s+/i, '$1/$2 ')
    .replace(/^(\d+)\s*,\s*(?:[^,]+,)?\s*(\d+)\s+/i, '$1/$2 ')
    // "118-120 Boronia Road" is a street-number range, not unit 118 at number
    // 120, so a dash is left as typed (Michael Piccoli, 21 Oct 2026).
    .replace(/^UNIT\s+/i, '');

  // 3. Title Case and keep states uppercase
  const words = formatted.split(/\s+/);
  
  const formattedWords = words.map((word, index) => {
    const hasComma = word.endsWith(',');
    const cleanWord = word.replace(/,$/, '').toUpperCase();

    // Keep states uppercase
    if (STATES.includes(cleanWord)) {
      return cleanWord + (hasComma ? ',' : '');
    }

    // Expand abbreviations (only if not the first word)
    if (ABBREVIATIONS[cleanWord] && index > 0) {
      // "ST" is ambiguous: street-type ("Park St") vs Saint suburb prefix ("St Kilda").
      // Treat it as Saint when there's no trailing comma and the next word is an
      // alphabetic name that isn't a state code.
      let isSaintPrefix = false;
      if (cleanWord === 'ST' && !hasComma) {
        const nextWord = words[index + 1];
        if (nextWord) {
          const nextClean = nextWord.replace(/[,.]$/, '').toUpperCase();
          if (!STATES.includes(nextClean) && /^[A-Z]+$/.test(nextClean)) {
            isSaintPrefix = true;
          }
        }
      }
      if (!isSaintPrefix) {
        return ABBREVIATIONS[cleanWord] + (hasComma ? ',' : '');
      }
    }

    // Default: Title Case
    // Handle cases like "3/25" (numbers with slash shouldn't be lowercased)
    if (word.includes('/')) {
        return word;
    }
    
    // Simple Title Case: First letter upper, rest lower
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  let result = formattedWords.join(' ');

  // 4. Optionally remove state and postcode, and dedupe repeated parts
  if (short) {
    const statesAlt = STATES.join('|');
    // Strip embedded "STATE POSTCODE", standalone state, or standalone postcode from each comma-segment
    const stripStatePostcode = (part) => part
      .replace(new RegExp(`\\b(?:${statesAlt})\\b\\s*\\d{4}\\b`, 'gi'), '')
      .replace(new RegExp(`\\b(?:${statesAlt})\\b`, 'gi'), '')
      // Strip a bare 4-digit postcode, but NOT a 4-digit number that is part of a
      // unit/street number like "1003/245" — the "/" guard keeps those intact.
      .replace(/(?<![\d/])\d{4}(?![\d/])/g, '')
      .replace(/\s+/g, ' ')
      .trim();

    const seen = new Set();
    const parts = result.split(',')
      .map(p => stripStatePostcode(p))
      .filter(p => {
        if (!p) return false;
        const key = p.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    result = parts.join(', ');
  }

  return result.trim().replace(/,$/, '');
};

/**
 * Title-cases a suburb that arrived in capitals or lower case ("ELWOOD" ->
 * "Elwood", "pascoe vale" -> "Pascoe Vale"). Mixed case is left as typed.
 */
export const formatSuburb = (suburb) => {
  const s = String(suburb || '').trim().replace(/\s+/g, ' ');
  if (!s || (s !== s.toUpperCase() && s !== s.toLowerCase())) return s;
  return s.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

/**
 * Joins a street line and a suburb in the house style "24 Renown Street,
 * Coburg North" (Michael Piccoli, 5 Oct 2026): suburb in Title Case, one comma
 * between them, suburb not repeated. If the street already ends with the
 * suburb but has no comma ("24 Renown Street Coburg North"), the comma goes in.
 */
export const joinStreetAndSuburb = (street, suburb) => {
  const st = String(street || '').trim().replace(/,\s*$/, '');
  const sub = formatSuburb(suburb);
  if (!sub) return st;
  if (!st) return sub;
  const escaped = sub.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const m = st.match(new RegExp(`^(.*?\\S)\\s*(,?)\\s+${escaped}$`, 'i'));
  if (m) {
    // Only when a real street sits in front, never a bare number.
    if (/\d/.test(m[1]) && /[A-Za-z]{2,}/.test(m[1])) return `${m[1].replace(/,$/, '')}, ${sub}`;
    return st;
  }
  if (st.toLowerCase().includes(sub.toLowerCase())) return st;
  return `${st}, ${sub}`;
};

/**
 * Combines address and suburb into a single string without repetition.
 * Mirrored from Buyer Portal but using CRM's formatAddress cleaner.
 * 
 * @param {object} property - Property object with address, city/suburb, state, postal_code
 * @returns {string} - Formatted short address (e.g. "123 Main St, Richmond")
 */
export const formatShortAddress = (property) => {
    if (!property) return "Address Not Available";

    const mode = property.address_publish_mode || "full";
    const suburb = property.city || property.suburb || "";

    // Honor the per-property address privacy setting from the CRM Add/Edit form.
    if (mode === "suburb") {
        return suburb ? formatSuburb(suburb) : "Address Not Available";
    }

    if (mode === "street_suburb") {
        const street = formatAddress(property.street_name || property.street || "", { short: true });
        return joinStreetAndSuburb(street, suburb) || "Address Not Available";
    }

    // Prioritize full_address if provided by backend (most reliable for CRM properties)
    if (property.full_address) return joinStreetAndSuburb(formatAddress(property.full_address, { short: true }), suburb);

    let street = property.address || property.street || "";

    // Clean street first using existing formatter
    street = formatAddress(street, { short: true });

    return joinStreetAndSuburb(street, suburb) || "Address Not Available";
};
